import React, { FC, useEffect, useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { isCardComplete } from "../../helper/payment";

interface Props {
  setStripe: (stripe: any) => void;
  setElements: (elements: any) => void;
  setError: (error: string | null) => void;
  error: string | null;
  formRef: React.RefObject<HTMLFormElement>; // Declare a new prop for formRef
  handleSubmit: (stripe: any, elements: any) => Promise<void>; // Define a prop for the handleSubmit function
  isLoading?: boolean; // Add a prop for isLoading
  disabled?: boolean;
  submitHandler: () => Promise<void>; // Define a prop for the handleSubmit function
  onCardFieldChange?: (elementType: string, state: { empty: boolean; complete: boolean }) => void;
  formError?: Record<string, string[]>;
}

const CheckoutForm: FC<Props> = ({
  setStripe,
  setElements,
  setError,
  error,
  formRef,
  handleSubmit,
  isLoading,
  disabled = false,
  submitHandler,
  onCardFieldChange,
  formError = {},
}) => {
  const stripe = useStripe();
  const elements = useElements();
  // Confirm stays grey until the card is filled in completely.
  const [cardholderName, setCardholderName] = useState("");
  const [completeFields, setCompleteFields] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setStripe(stripe);
    setElements(elements);
  }, [stripe, elements, setStripe, setElements]);

  const handleChange = (e: any) => {
    if (e.error) {
      setError(e.error.message);
    } else {
      setError(null);
    }
    setCompleteFields((fields) => ({ ...fields, [e.elementType]: Boolean(e.complete) }));
    if (onCardFieldChange) {
      onCardFieldChange(e.elementType, { empty: e.empty, complete: e.complete });
    }
  };
  const cardComplete = isCardComplete(cardholderName, completeFields);

  const { t } = useTranslation();

  return (
    <form ref={formRef} className='bookingForm'>
      <div className='colsHolder'>
        <div className='chCol chCol12'>
          <div className='formGroup'>
            <label>
              {t("name_on_card")}
              <input
                type='text'
                id='inputCardholderName'
                className='formControl'
                placeholder={t("name_on_card")}
                onChange={(event) => {
                  setCardholderName(event.target.value);
                  if (onCardFieldChange && formError["card.name"]) {
                    onCardFieldChange("cardName", { empty: false, complete: true });
                  }
                }}
              />
            </label>
            {formError["card.name"] && (
              <p className='error-message'>{formError["card.name"][0]}</p>
            )}
          </div>
        </div>
        <div className='chCol chCol12'>
          <div className='formGroup'>
            <label>
              {t("card_number")}
              <CardNumberElement
                className='formControl'
                onChange={handleChange}
              />
            </label>
            {formError["card.number"] && (
              <p className='error-message'>{formError["card.number"][0]}</p>
            )}
          </div>
        </div>
        <div className='chCol chCol6'>
          <div className='formGroup'>
            <label>
              {t("expiry_date")}
              <CardExpiryElement
                className='formControl'
                onChange={handleChange}
              />
            </label>
            {formError["card.expiry"] && (
              <p className='error-message'>{formError["card.expiry"][0]}</p>
            )}
          </div>
        </div>
        <div className='chCol chCol6'>
          <div className='formGroup'>
            <label>
              {t("cvc")}
              <CardCvcElement className='formControl' onChange={handleChange} />
            </label>
            {formError["card.cvc"] && (
              <p className='error-message'>{formError["card.cvc"][0]}</p>
            )}
          </div>
        </div>
      </div>
      {error && <p className='error-message'>{error}</p>}
      <div className='submitBtn margin-top-added'>
        <button
          disabled={isLoading || disabled || !cardComplete}
          className='btnPrimary primaryBtnColor'
          onClick={submitHandler}
        >
          {t("confirm")}
        </button>
      </div>
    </form>
  );
};

export default CheckoutForm;
